import type { SortingAction, SortingCard, SortingCounters, SortingDecisionRequest, SortingProgress } from '@nest-recruit/shared';
import { ApiError } from '../../lib/api-client';

// The bookkeeping behind SCR-01's instant feel, pure so it is tested without a
// browser. The screen shows a decision the moment a key is pressed and sends it
// afterwards (non-negotiable #11); these functions say what the screen should show
// while some of those sends are still on their way.

/** One thing waiting to reach the server, in the order it was done. */
export type PendingOp =
  | {
      readonly kind: 'decision';
      readonly clientDecisionId: string;
      readonly action: SortingAction;
      readonly card: SortingCard;
      readonly reasonCode: string | null;
      readonly poolIds: readonly string[];
      readonly newPoolNames: readonly string[];
      /** DECISIONS-LOG 2026-10-19 Group 3 Q2: "Also add to CV Bank", ticked in the reject box. */
      readonly addToCvBank: boolean;
      readonly dwellMs: number;
    }
  | { readonly kind: 'undo'; readonly id: string; readonly decisionId: string; readonly card: SortingCard; readonly action: SortingAction }
  | { readonly kind: 'note'; readonly id: string; readonly applicationId: string };

/** The last decisions this session, newest first — what Ctrl+Z walks back through. */
export interface LocalDecision {
  readonly clientDecisionId: string;
  /** Known once the server has recorded it. */
  readonly decisionId: string | null;
  readonly action: SortingAction;
  readonly card: SortingCard;
  readonly cannotUndoReason: string | null;
}

const COUNTER_OF: Readonly<Record<SortingAction, keyof SortingCounters>> = {
  shortlist: 'shortlisted',
  maybe: 'maybe',
  reject: 'rejected',
  pool: 'pooled',
};

export type DecisionOp = Extract<PendingOp, { readonly kind: 'decision' }>;

/** What every decision sends, whatever it is: which card, the note with it, how long it was read. */
export interface DecisionBase {
  readonly clientDecisionId: string;
  readonly applicationId: string;
  readonly note?: string;
  readonly noteActivityId: string | null;
  readonly dwellMs: number;
}

/** The request a queued decision becomes. Only a reject carries the CV Bank tick (Group 3 Q2); Pool has no such box. */
export function decisionRequestOf(op: DecisionOp, base: DecisionBase): SortingDecisionRequest {
  if (op.action === 'shortlist' || op.action === 'maybe') return { ...base, action: op.action };
  const rejection = { reasonCode: op.reasonCode ?? '', poolIds: [...op.poolIds], newPoolNames: [...op.newPoolNames] };
  if (op.action === 'reject') return { ...base, ...rejection, action: 'reject', addToCvBank: op.addToCvBank };
  return { ...base, ...rejection, action: op.action };
}

/** The server's counters, plus what is still on its way (F8.10 "counters accurate throughout"). */
export function displayCounters(server: SortingCounters, pending: readonly PendingOp[]): SortingCounters {
  const counters = { ...server };
  for (const op of pending) {
    if (op.kind === 'decision') counters[COUNTER_OF[op.action]] += 1;
    if (op.kind === 'undo') {
      counters[COUNTER_OF[op.action]] = Math.max(0, counters[COUNTER_OF[op.action]] - 1);
      counters.undone += 1;
    }
  }
  return counters;
}

/**
 * "34 of 180": the card on screen is the one after everything decided so far. A Maybe isn't
 * decided — it comes back round (§L31) — so it counts neither way.
 */
export function displayProgress(server: SortingProgress, pending: readonly PendingOp[]): { readonly position: number; readonly total: number } {
  const decisions = pending.filter((op) => op.kind === 'decision' && op.action !== 'maybe').length;
  const undos = pending.filter((op) => op.kind === 'undo' && op.action !== 'maybe').length;
  const decided = Math.max(0, server.decided + decisions - undos);
  return { position: Math.min(decided + 1, Math.max(server.total, 1)), total: server.total };
}

/** Cards joining the window, never twice, never one already decided. */
export function mergeCards(window: readonly SortingCard[], incoming: readonly SortingCard[], leaving: ReadonlySet<string>): readonly SortingCard[] {
  const present = new Set(window.map((card) => card.applicationId));
  return [...window, ...incoming.filter((card) => !present.has(card.applicationId) && !leaving.has(card.applicationId))];
}

/** Every application the screen holds or is still deciding: the server must not hand these out again. */
export function heldIds(window: readonly SortingCard[], pending: readonly PendingOp[]): readonly string[] {
  const ids = new Set(window.map((card) => card.applicationId));
  for (const op of pending) {
    if (op.kind === 'decision') ids.add(op.card.applicationId);
  }
  return [...ids];
}

/**
 * A failure worth retrying — the connection, or the server having a bad moment —
 * as opposed to the server saying no (a 4xx), which retrying would only repeat.
 */
export function isRetryable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status === 0 || error.status >= 500;
}
